import { useNavigate, useParams } from 'react-router-dom';
import { useState, useEffect, useContext, useRef } from 'react';
import { Button, PersonAvatar, IconButton } from '@teo/components';
import { useGet, put } from '@/query/fetchHooks';
import { ArrowLeft, Tag, CrossFilled, Pencil } from '@teo/components/icons';
import { useTranslation } from 'react-i18next';
import { nthLastIndexOf, convertToTime } from '@/util';
import { format } from 'date-fns';
import AddLibraryStepsContainer from './AddLibraryStepsContainer/AddLibraryStepsContainer';
import { Userwithorganisationwithidps } from '@/models';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { uploadImage } from '@/query/documents';

const LibraryDetailLesson = () => {
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(window.location.search);
  const prevPage = queryParams.get('type');
  const { t } = useTranslation();
  const { lessonId } = useParams();
  const [isAddLabrary, setIsAddLabrary] = useState(false);
  const [lesson, setLesson] = useState<any>(null);
  const [tags, setTags] = useState<any>();
  const [chapterTree, setChapterTree] = useState<any>(null);
  const [extraLes, setExtraLes] = useState<any>(null);

  const { auth } = useContext(AuthContext);
  const authUser = auth?.user as Userwithorganisationwithidps;
  const isAdmin = (authUser?.role || 0) >= 90;
  const imageInputRef = useRef<HTMLInputElement>(null);
  const useTeoLib = authUser?.useTeoLib;

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await uploadImage(file, 920, 920);
    await put(`/lesson/${lessonId}`, { lessonImage: url });
    setLesson((prev: any) => ({ ...prev, lessonImage: url }));
    e.target.value = '';
  };

  const basePath = window.location.pathname.slice(
    0,
    nthLastIndexOf(window.location.pathname, '/', 2)
  );

  const examplePath = window.location.pathname.slice(
    0,
    nthLastIndexOf(window.location.pathname, '/', 3)
  );

  useGet(`/lessonimports`, {
    organisationId: `${authUser.activeOrganisationId}${
      useTeoLib ? ',OR,organisationId=null' : ''
    }`,
    lessonId: lessonId,
    onSuccess: (result: any) => {
      setLesson(result?.data[0] || null);
    },
  });

  useGet(`/lessonextrainfos`, {
    rootId: lessonId,
    onSuccess: (result: any) => {
      setExtraLes(result?.data || null);
    },
  });

  useEffect(() => {
    if (lesson?.tag) {
      const data = lesson?.tag?.split(';;');
      const filteredTags = data.filter(
        (item: any) => item.toLowerCase() !== lesson?.name?.toLowerCase()
      );
      setTags(
        filteredTags
          ?.filter((x: any) => x?.startsWith('tag:'))
          ?.map((el: any) => el?.split('tag:')[1])
      );
    }
  }, [lesson?.tag]);

  const handleGoBack = () => {
    navigate(prevPage == 'lesson' ? `${basePath}?type=lesson` : `${basePath}`);
  };

  if (!lesson) return null;

  if (isAddLabrary) {
    return (
      <div className="fixed top-0 left-0 z-[100] h-screen w-screen overflow-y-auto bg-white">
        <div className="flex min-h-20 flex-col justify-start py-2 pr-2 sm:flex-row sm:items-center">
          <div
            data-testid="clouseBtn"
            className="flex min-h-20 items-center justify-start"
          >
            <IconButton
              className="my-auto mx-3 sm:mx-6"
              Icon={CrossFilled}
              variant="ghost"
              onClick={() => {
                setIsAddLabrary(false);
              }}
            />
            <div className="flex items-center">
              <div className="mr-5 w-20 min-w-20">
                <img
                  src={
                    lesson?.publicationImage
                      ? lesson?.publicationImage
                      : '/assets/images/opleidingen.jpg'
                  }
                  alt={lesson?.name}
                  className="h-12 w-full max-w-[72px] rounded-md object-cover"
                />
              </div>
              <div className="flex flex-wrap">
                <p>
                  {t(`pages.create_library.title`)}:
                  <span className="ml-2 font-semibold">{lesson?.name}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
        <AddLibraryStepsContainer
          node={lesson}
          chapterTree={chapterTree}
          lesExtraInfo={extraLes}
        />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col">
        <div className="my-8 flex">
          <Button
            className="flex items-center gap-3"
            variant="text"
            onClick={() => handleGoBack()}
          >
            <ArrowLeft className="h-5 w-5" />
            <p>{t(`pages.create_library.back_to_library`)}</p>
          </Button>

          <div className="ml-auto flex gap-2">
            <Button
              variant="outline"
              onClick={() =>
                window.open(
                  `${examplePath}/library/lesson/${lesson?.lessonId}/preview?new_tab=true`,
                  '_blank'
                )
              }
            >
              {t('button.example')}
            </Button>
            <Button onClick={() => setIsAddLabrary(true)}>
              {t(`pages.create_library.use_lesson`)}
            </Button>
          </div>
        </div>
        <div className="group relative mb-6">
          <img
            src={
              lesson?.lessonImage
                ? lesson?.lessonImage
                : lesson?.publicationImage
                ? lesson?.publicationImage
                : '/assets/images/opleidingen.jpg'
            }
            alt={lesson?.name}
            className="max-h-80 w-full rounded-md object-cover"
          />
          {isAdmin && (
            <>
              <button
                className="absolute bottom-2 right-2 flex items-center gap-1 rounded bg-black/50 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100"
                onClick={() => imageInputRef.current?.click()}
              >
                <Pencil className="h-3 w-3" />
                {t('button.edit')}
              </button>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
              />
            </>
          )}
        </div>
        <div className="flex flex-col">
          <h1 className="mb-6 text-xl">
            <span className="pr-2 font-normal text-grey-06">
              {t(`pages.create_library.lesson`)}:
            </span>
            {lesson?.name}
          </h1>
          <div className="mb-6 flex flex-wrap items-center">
            <PersonAvatar
              inline
              id={lesson?.authorId}
              name={lesson?.authorName}
              imageSrc={lesson?.authorPicture ? lesson?.authorPicture : ''}
            ></PersonAvatar>
            <div>
              <p className="text-xs text-grey-07">
                {lesson?.authorName && (
                  <>
                    {t('pages.create_edit_course.library.written_by')}{' '}
                    {lesson?.authorName}
                  </>
                )}
                <span className="-mt-1 px-2">|</span>
              </p>
            </div>
            <p className="text-xs text-grey-07">
              {lesson?.updatedAt &&
                t('pages.create_edit_course.library.last_updated') +
                  ' ' +
                  format(new Date(lesson?.updatedAt), 'dd MMM yyyy')}
            </p>
            {lesson?.estimatedTime > 0 && (
              <span className="flex flex-row items-center gap-1 text-xs text-grey-07">
                <span className="-mt-1 px-2">|</span>
                <p>
                  <span className="pr-1">
                    {t('pages.create_edit_course.library.reading_time')}
                  </span>
                  {convertToTime(
                    Math.abs(lesson?.estimatedTime),
                    t('pages.learn_index.hour'),
                    t('pages.learn_index.minute')
                  )}
                </p>
              </span>
            )}
          </div>
          {tags?.length > 0 && (
            <div className="mb-5 flex flex-wrap items-center gap-3">
              <Tag className="h-5 w-5 text-grey-07" />
              {tags?.map((el: any, i: any) => {
                return (
                  <div
                    key={i}
                    className="inline-flex items-center gap-1 rounded bg-grey-transparent-02 py-0.5 px-2 text-sm font-medium text-grey-08"
                  >
                    {el}
                  </div>
                );
              })}
            </div>
          )}

          <p className="mb-5 text-sm text-grey-08">{lesson?.description}</p>
        </div>
      </div>
    </>
  );
};

export default LibraryDetailLesson;
